import { defineComponent, reactive, SetupContext, ref } from '@vue/composition-api';
import vueRouter from '@/router/index';
import { TableHeader, CreateSendFilesList, FileData, ErrorTableType, CheckOrganizationIdList } from '@/shared/type';
import { DataOptions } from 'vuetify';
import vueFilePond from 'vue-filepond';
import { FileOrigin, FilePondFile } from 'filepond';
import FilePondPluginGetFile from 'filepond-plugin-get-file';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-get-file/dist/filepond-plugin-get-file.min.css';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import HttpStatus from 'http-status-codes';
import SystemConst from '@/shared/system-const';
import MessageConst from '@/shared/message-const';
import CommonFunction from '@/shared/common-function';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };
    collectiveSettingDialog: boolean;
    individualSettingDialog: boolean;
    selectedItemAll: boolean;
    sendDataTableHeaders: TableHeader[];
    sendItems: CreateSendFilesList[];
    expanded: CreateSendFilesList[];
    closeWarningDialog: boolean;
    confirmDialog: boolean;
    fileInputDialog: boolean;
    inputFolderParam: number;
    visibleFolderStructure: boolean;
    isAllSelected: boolean;
    showErrorMessageDialog: boolean;
    fileErrorItems: ErrorTableType[];
    collectiveTitle: string;
    collectiveMessage: string;
    tmpTitle: string;
    tmpMessage: string;
    validateValues: ValidateType;
    attachmentFileErrorMessage: string;
    isAttachmentFileValid: boolean;
    isClear: boolean;
    attachmentDetailFileErrorMessage: string;
    isAttachmentDetailFileValid: boolean;
    isClearExpand: boolean;
    total: number;
    pageOptions: DataOptions;
    isLoading: boolean;
    isLoadingSelectAll: boolean;
    tmpImportantInfoId: string;
    beforeExpandItem: CreateSendFilesList[];
    afterExpandItem: CreateSendFilesList[];
    isUseEscapeExpand: boolean;
    expandClose: boolean;
    isAttachmentZipSetting: boolean;
    isAllCompletionMark: boolean;
    tmpSelectedData: CheckOrganizationIdList;
};

// バリデーション用パラメーター
interface ValidateType {
    [key: string]: ValidateValues
}

// バリデーション用パラメーターデータ
type ValidateValues = {
    parameterName: string;                                   // パラメーター名
    value: string;                                           // パラメーター値
}

// ファイルアップロード
const FilePond = vueFilePond(FilePondPluginGetFile);

/**
 * 重要情報のお知らせ：送付データ登録
 *
 * 送付データ登録画面を表示する
 */
export default defineComponent({
    components: { FilePond, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const tableHeight = SystemConst.TABLE_HEIGHT.IMPORTANT_INFORMATION_SENDING_LIST;    // テーブル高さ調整用変数
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                               // 1ページあたりの行数候補（10, 50, 100）
        const pageNation = ref<number>(1);                                                  // ページ番号（状態管理用）
        const itemsPerPage = ref<number>(linesPerPage[0]);                                  // 1ページあたりの行数（状態管理用、デフォルト値：10）
        const sortBy = ref<string[]>([]);                                                   // ソートカラム（状態管理用）
        const sortDesc = ref<boolean[]>([]);                                                // ソート順（状態管理用）
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;                            // テーブルのデータ取得中に表示するテキスト
        const displayMessage = MessageConst.INFO_EXPAND_DATA_DELETE;                        // 展開行を閉じるダイアログの警告メッセージ
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },
            collectiveSettingDialog: false,                                                 // タイトル・メッセージ一括設定ダイアログ表示
            individualSettingDialog: false,                                                 // タイトル・メッセージ個別設定ダイアログ表示
            validateValues: {},                                                             // 取得したバリデーションの値
            attachmentFileErrorMessage: '',                                                 // 一括登録用添付ファイルのバリデーションメッセージ文字列
            isAttachmentFileValid: true,                                                    // 一括登録用添付ファイルのバリデーション結果
            isClear: false,                                                                 // 一括登録用添付ファイル入力欄のクリア中
            attachmentDetailFileErrorMessage: '',                                           // 行展開後添付ファイルのバリデーションメッセージ文字列
            isAttachmentDetailFileValid: true,                                              // 行展開後添付ファイルのバリデーション結果
            isClearExpand: false,                                                           // 行展開後添付ファイル入力欄のクリア中
            selectedItemAll: false,                                                         // 全選択チェックボックスの値
            sendDataTableHeaders: [],                                                       // ヘッダー情報
            sendItems: [],                                                                  // 送付情報（テーブル全データ）
            expanded: [],                                                                   // テーブル展開中情報
            closeWarningDialog: false,                                                      // 展開情報破棄確認ダイアログ表示切替
            confirmDialog: false,                                                           // データ確定ダイアログ表示
            fileInputDialog: false,                                                         // ファイル取り込みダイアログ表示
            inputFolderParam: SystemConst.ATTACHMENT_FOLDER_PARAM.AFFILIATE_CODE,           // 添付ファイル一括設定（1: 組織ID、2: 所属コード）
            visibleFolderStructure: false,                                                  // 添付ファイル取り込みの構成表示
            isAllSelected: false,                                                           // 全選択チェックボックス操作フラグ
            showErrorMessageDialog: false,                                                  // エラーメッセージダイアログ表示切り替え
            fileErrorItems: [],                                                             // バリデーションエラーのデータ表示内容
            collectiveTitle: '',                                                            // 一括設定用タイトル
            collectiveMessage: '',                                                          // 一括設定用メッセージ
            tmpTitle: '',                                                                   // 個別設定用タイトル
            tmpMessage: '',                                                                 // 個別設定用メッセージ
            total: 0,                                                                       // 送付一覧の総件数
            pageOptions: {                                                                  // ページ情報を保存する変数
                page: 1, itemsPerPage: linesPerPage[0], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            isLoading: false,                                                               // テーブルがロード中: true ロード中でない: false
            isLoadingSelectAll: false,                                                      // 全選択チェックボックスがロード中: true ロード中でない: false
            tmpImportantInfoId: '0',                                                        // 一時重要情報ID
            beforeExpandItem: [],                                                           // 展開されていたデータ
            afterExpandItem: [],                                                            // 展開するデータ
            isUseEscapeExpand: false,                                                       // 個別タイトルメッセージ更新可否
            expandClose: true,                                                              // 展開行を閉じるか判別（true: 閉じる, false: 行更新のため、閉じない）
            isAttachmentZipSetting: false,                                                  // 添付ファイル一括設定フラグ（true: 添付ファイル一括設定中の処理）
            isAllCompletionMark: false,                                                     // 全件登録完了判別（true: すべて完了, false: 未完）
            tmpSelectedData: {}                                                             // チェック済み送付先組織
        });
        // 完了マークがついているデータのシーケンス
        const completionMarkData: number[] = [];
        const tmpEscapeExpand = { title: '', message: '' };
        const tableHeaderClass: string[] = ['tableHeader subtitle-2'];
        const router = vueRouter;
        const form = ref<any>(null);
        const bulkForm = ref<any>(null);                                                    // タイトル・メッセージ一括設定ダイアログバリデーションチェック
        const expandForm = ref<any>(null);                                                  // タイトル・メッセージ個別設定ダイアログバリデーションチェック
        const attachmentPondRef = ref<any>(null);                                           // FilePond（添付ファイル一括設定）への参照
        const expandPondRef = ref<any>(null);                                               // FilePond（行展開後）への参照
        let errors: string[] = [];                                                          // 添付ファイル一括設定のエラーメッセージ
        const commonRef = ref<any>(null);                                                   // メッセージダイアログ（HTTP通信失敗のエラー画面遷移用）
        const commonAddRef = ref<any>(null);                                                // 全体登録共通メッセージダイアログへの参照
        const stepper = <number>3;                                                          // ステッパーの3段階目
        const selectCreation = {                                                            // フォルダー設定ラジオボタン名称
            affiliateCode: SystemConst.ATTACHMENT_FOLDER_NAME.AFFILIATE_CODE,
            organizationId: SystemConst.ATTACHMENT_FOLDER_NAME.ORGANIZATION_ID
        };
        const attachmentTreeFormat = [                                                      // 添付データファイル説明
            {
                name: 'ルートフォルダ',
                children: [
                    {
                        name: 'フォルダ（フォルダ名には組織ID / 所属コードの値を設定してください）',
                        children: [
                            { files: 'file', name: '添付ファイル1（対象組織に送付したいファイルを設定してください）' },
                            { files: 'file', name: '添付ファイル2' },
                            { files: 'file', name: '添付ファイル3' }
                        ]
                    }
                ]
            }
        ];
        const sendFilesTableHeaders: TableHeader[] = [
            // 送付データ一覧ヘッダ情報
            // チェックボックス
            {
                text: '',
                value: 'dataTableSelect',
                sortable: false,
                width: '4%',
                class: tableHeaderClass
            },
            // 確定済みアイコン
            {
                text: '',
                value: 'icon',
                sortable: false,
                width: '5%',
                class: tableHeaderClass
            },
            // 組織ID
            {
                text: '組織ID',
                value: 'organizationId',
                width: '12%',
                class: tableHeaderClass
            },
            // 所属コード
            {
                text: '所属コード',
                value: 'affiliationCode',
                width: '20%',
                class: tableHeaderClass
            },
            // 組織型
            {
                text: '組織型',
                value: 'organizationType',
                width: '20%',
                class: tableHeaderClass
            },
            // 送付先組織
            {
                text: '送付先組織',
                value: 'organizationName',
                width: '20%',
                class: tableHeaderClass
            },
            // 送付区分
            {
                text: '送付区分',
                value: 'sendDivision',
                width: '15%',
                class: tableHeaderClass
            },
            // 行展開
            {
                text: '',
                value: 'data-table-expand',
                sortable: false,
                width: '4%',
                class: tableHeaderClass
            }
        ];
        // バリデーション用最大桁数
        const maxLength = {
            // タイトル
            title: SystemConst.TMP_IMPORTANT_INFO_MAX_LENGTH.TITLE,
            // メッセージ
            message: SystemConst.TMP_IMPORTANT_INFO_MAX_LENGTH.MESSAGE
        };
        // タイトルのバリデーション
        const titleRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['タイトル']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['title'], 'タイトル')
        ];
        // メッセージのバリデーション
        const messageRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['メッセージ']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['message'], 'メッセージ')
        ];

        // 添付ファイル一括登録のバリデーション
        const bulkAddAttachmentFileRules = [
            () => {
                // バリデーションはAPIの結果を利用するため、
                // APIが呼ばれていない段階では以降を実行しない
                if (!('attachmentFileCount' in state.validateValues)) {
                    return;
                }

                // ファイルの情報を得る
                const files: FilePondFile[] = attachmentPondRef.value.getFiles();

                // ファイルが最低1つ登録されていることを確認
                if (files.length === 0) {
                    return MessageConst.E_FILE_REQUIRED;
                }

                // 引数に以下を設定して、validateFiles()を呼び出し、バリデーションを設定する
                //   登録ファイル数、ファイルサイズ、ファイル拡張子、メッセージ
                return validateFiles(files,
                    Number(state.validateValues['attachmentFileCount'].value),
                    Number(state.validateValues['attachmentFileSize'].value),
                    state.validateValues['attachmentFileExtension'].value,
                    '添付データ');
            }
        ];

        // 行展開後添付ファイルのバリデーション
        const expandAttachmentFilesRules = [
            () => {
                // バリデーションはAPIの結果を利用するため、
                // APIが呼ばれていない段階では以降を実行しない
                if (!('attachmentDetailFileCount' in state.validateValues)) {
                    return;
                }

                // ファイルの情報を得る
                const files: FilePondFile[] = expandPondRef.value.getFiles();

                // ファイルが最低1つ登録されていることを確認
                // (新しく展開した行でファイルが未設定の場合はエラーを出さない)
                if ((state.beforeExpandItem[0].expandItem.title !== null && files.length === 0) ||
                    (state.beforeExpandItem[0].expandItem.attachmentFiles.length !== 0 && files.length === 0)) {
                    return MessageConst.E_FILE_REQUIRED;
                }

                // ファイル名が重複していないか確認する
                if (Array.from(new Set(files.map(e => e.file.name))).length !== files.length) {
                    // 重複を排除した配列の長さが異なれば、重複あり
                    return MessageConst.E_FILE_NAME_DUPLICATE;
                }

                // 引数に以下を設定して、validateFiles()を呼び出し、バリデーションを設定する
                //   登録ファイル数、ファイルサイズ、ファイル拡張子、メッセージ
                return validateFiles(files,
                    Number(state.validateValues['attachmentDetailFileCount'].value),
                    Number(state.validateValues['attachmentDetailFileSize'].value),
                    state.validateValues['attachmentDetailFileExtension'].value,
                    '添付ファイル');
            }
        ];

        /**
         * ファイルバリデーション
         * 
         * @param files 対象ファイル
         * @param cnt バリデーション値（ファイル数）
         * @param size バリデーション値（ファイルサイズ）
         * @param ext バリデーション値（拡張子）
         * @param message メッセージ（'基本データ' or '添付データ'）
         * @returns 正常の場合true エラーの場合メッセージ文字列
         */
        function validateFiles(files: FilePondFile[], cnt: number, size: number, ext: string, message: string) {
            // ファイル数が超過していないか
            if (files.length > cnt) {
                return CommonFunction.replaceArray(
                    MessageConst.E_FILE_FILE_COUNT, [
                    message,
                    cnt + '件のファイル']);
            }

            // ファイルサイズが超過または0バイトでないか
            for (const key in files) {
                if (files[key].fileSize > size) {
                    // 単位をメッセージの単位"KB"に合わせるため、fileSizeを1024で割る
                    return CommonFunction.replaceArray(
                        MessageConst.E_FILE_MAX_SIZE, [size / 1024]);
                } else if (files[key].fileSize === 0) {
                    // ファイルサイズが0バイトの場合、エラーメッセージを返す
                    return MessageConst.E_FILE_NO_DATA;
                }
            }

            // 拡張子が登録対象か
            for (const key in files) {
                // 登録しようとするファイルの拡張子を得る
                const parts = files[key].file.name.split(/\.(?=[^.]+$)/);
                const fileExtension = parts[1];

                // m_parametersの拡張子一覧
                const validateExtension = ext.split(',');

                if (validateExtension.indexOf(fileExtension) === -1) {
                    // 比較した結果、拡張子一覧に含まれていないとき、エラーメッセージを返す
                    return CommonFunction.replaceArray(
                        MessageConst.E_FILE_EXTENSION, [ext]);
                }
            }

            return true;
        }

        /**
         * created
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-file-send');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', '重要情報のお知らせ登録');

            // 添付ファイルバリデーションのパラメータを取得する
            const response = await ctx.root.$store.dispatch('importantInformation/getCreateSendingParams');

            // レスポンスが正常でない場合
            if (response.status !== HttpStatus.OK) {
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // バリデーションパラメータをstate.validateValuesに設定する
            state.validateValues = response.data.parameters;

            // ヘッダー設定
            state.sendDataTableHeaders = sendFilesTableHeaders;

            // チェック済み送付先組織の初期化
            state.tmpSelectedData = {};
            // 送付一覧取得
            getSendingData();
        }

        /**
         * 送付一覧テーブルデータ取得
         */
        async function getSendingData() {
            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // ページ情報（string型へ変換）
            const page = String(state.pageOptions.page);
            // 1ページ当たりの行数（string型へ変換）
            const itemsPerPage = String(state.pageOptions.itemsPerPage);
            // ソートキー（v-data-tableのheadersに設定した'value'）
            const sortKeyParam = state.pageOptions.sortBy.join(',');
            // ソート順（false => 'asc' true => 'desc'へ変換）
            const sortOrderParam = state.pageOptions.sortDesc.map(e => SystemConst.SORT_ORDER_MAPPING[Number(e)]).join(',');

            // API実行
            // GET: /api/important-information/create-sending
            // パラメータ: ページ情報
            //             1ページ当たりの行数
            //             ソートキー
            //             ソート順
            const response = await ctx.root.$store.dispatch('importantInformation/getCreateSendingList',
                {
                    page: page,
                    itemsPerPage: itemsPerPage,
                    sortKey: sortKeyParam,
                    sortOrder: sortOrderParam
                }
            );

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する
                state.isLoading = false;

                // ステータスコード: 404の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    // step2の画面へ戻る
                    const beforeDisplayPath = '/important-information/select-organization';
                    commonRef.value.displayErrorMessageTransitionDialog(response, beforeDisplayPath);
                    return;
                }
                // それ以外のステータスコードの場合
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 一覧のデータを設定する
            state.sendItems = response.data.sendingData;

            // 一覧の全データを繰り返す
            for (let itemCount = 0; itemCount < state.sendItems.length; itemCount++) {
                // 完了マークの格納済みチェック
                const index = completionMarkData.findIndex(element => element === state.sendItems[itemCount].sequence);
                if (index !== -1) {
                    // iconにtrueを格納し、完了チェックマークを追加する
                    ctx.root.$set(state.sendItems[itemCount], 'icon', true);
                } else {
                    // タイトル、メッセージ、添付ファイルが入力されている場合、完了マークを追加する
                    if (state.sendItems[itemCount].expandItem.title &&
                        state.sendItems[itemCount].expandItem.message &&
                        state.sendItems[itemCount].expandItem.attachmentFiles.length !== 0) {
                        completionMarkData.push(state.sendItems[itemCount].sequence);
                        ctx.root.$set(state.sendItems[itemCount], 'icon', true);
                    }
                }
            }

            // 全件登録済み判別
            state.isAllCompletionMark = response.data.isAllCompletionMark;

            // 一時重要情報IDをstate.tmpImportantInfoIdへ設定する
            state.tmpImportantInfoId = response.data.tmpImportantInfoId;

            // 総検索件数をstate.totalへ設定する
            state.total = response.data.totalCount;

            // 送付先組織一覧の個別チェックボックスの状態を更新する
            checkUpdate();
            // 送付先組織一覧の全選択チェックボックスの状態を更新する
            checkAllUpdate();

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * 送付一覧のページ/ソート情報変更に対するイベント
         * @param options ページ/ソート情報
         */
        function updateOptions(options: DataOptions) {
            // ページ情報をstateへ保存する
            state.pageOptions = options;

            // 状態管理用変数とoptionsの中身と比較して、いずれも変更されていなかった場合
            if (pageNation.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                // 処理を終了する
                return;
            }

            // ページ番号、1ページあたりの行数を設定する
            pageNation.value = options.page;
            itemsPerPage.value = options.itemsPerPage;

            // ソートキー、ソート順を設定する
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // 展開中の行を閉じる
            if (state.beforeExpandItem.length !== 0) {
                state.beforeExpandItem = [];
                closeExpand();
            }

            // 送付一覧取得
            getSendingData();
        }

        /**
         * 送付一覧の行の開閉時イベント
         * @param expand 展開中の行データ
         */
        function updateExpand(expand: CreateSendFilesList[]) {
            // 行が展開された場合
            if (state.beforeExpandItem.length === 0 && expand.length !== 0) {
                // openExpand() を呼び出す
                openExpand(expand[0]);
                return;
            }

            // 展開された行が変わった場合
            if (state.beforeExpandItem.length !== 0 && expand.length !== 0 && state.beforeExpandItem[0] !== expand[0]) {
                state.afterExpandItem[0] = expand[0];
                const files: FilePondFile[] = expandPondRef.value.getFiles();
                // 以下条件のいずれかが当てはまる場合、警告メッセージを表示
                //     タイトル・メッセージデータが保存された
                //     expandPondRefのファイル件数がexpand[0].expandItem.attachmentFilsesの件数と一致していない
                //     expandPondRefのtype: 'input'のデータがある
                if (state.isUseEscapeExpand ||
                    files.length !== state.beforeExpandItem[0].expandItem.attachmentFiles.length ||
                    files.findIndex(e => e.origin === FileOrigin.INPUT) !== -1) {
                    // ダイアログ表示切替
                    state.closeWarningDialog = true;
                } else {
                    // データが更新されていなかった場合
                    closeExpand();
                }
                return;
            }
            // 行が閉じられた場合
            if (state.beforeExpandItem.length !== 0 && expand.length === 0 && state.expandClose) {
                state.afterExpandItem = [];
                expand[0] = state.beforeExpandItem[0];
                const files: FilePondFile[] = expandPondRef.value.getFiles();
                // 以下条件のいずれかが当てはまる場合、警告メッセージを表示
                //     添付ファイル一括設定ではない
                //     タイトル・メッセージデータが保存された
                //     expandPondRefのファイル件数がexpand[0].expandItem.attachmentFilsesの件数と一致していない
                //     expandPondRefのtype: 'input'のデータがある
                if (!state.isAttachmentZipSetting && (state.isUseEscapeExpand ||
                    (typeof files !== 'undefined' &&
                        (files.length !== expand[0].expandItem.attachmentFiles.length ||
                            files.findIndex(e => e.origin === FileOrigin.INPUT) !== -1)))) {
                    // ダイアログ表示切替
                    state.closeWarningDialog = true;
                } else {
                    // データが更新されていなかった場合
                    if (!state.isAttachmentZipSetting) {
                        state.beforeExpandItem = [];
                    }
                    expand = [];
                    closeExpand();
                }
                // 添付ファイル一括設定をfalseにする
                state.isAttachmentZipSetting = false;

                return;
            }
        }

        /**
         * 行展開
         */
        function openExpand(expand: CreateSendFilesList) {
            // 行展開後に表示される添付ファイルのデータをexpandPondRefに設定する
            // vueで表示されるのを0.3秒待つ
            setTimeout(function () {
                // 行展開後に表示される添付ファイルのデータをexpandPondRefに設定する
                expand.expandItem.attachmentFiles.forEach((e: { fileName: string; fileSize?: number; fileSequence: number }) => {
                    expandPondRef.value.addFile(e.fileName, {
                        type: 'local',              // 'local':サーバーにあるファイル 'input':ユーザが追加したファイル
                        file: {
                            name: e.fileName,       // ファイル名
                            size: e.fileSize        // ファイルサイズ
                        },
                        metadata: {                 // ダウンロード時のURLを設定
                            // /api/important-information/create-sending/{一時重要情報ID}/{シーケンス}/{ファイルシーケンス}
                            url: `/api/important-information/create-sending/${state.tmpImportantInfoId}/${expand.sequence}/${e.fileSequence}`
                        }
                    });
                });
            }, 300);

            state.expandClose = true;
            state.beforeExpandItem[0] = expand;
            state.afterExpandItem = [];
        }

        /**
         * 警告メッセージ「はい」クリック時
         */
        function closeExpand() {
            // 添付ファイルのデータをpondRefから削除する
            expandPondRef.value.removeFiles();
            // ダイアログを閉じる
            state.closeWarningDialog = false;

            // state.expandedを初期化して展開している行を閉じる（updateExpand()が走る）
            state.expandClose = false;
            state.expanded = [];
            // 展開中のタイトル・メッセージを tmpEscapeExpandに保存している場合
            if (state.isUseEscapeExpand) {
                // 更新前データに上書き
                state.beforeExpandItem[0].expandItem.title = tmpEscapeExpand.title;
                state.beforeExpandItem[0].expandItem.message = tmpEscapeExpand.message;
                // 展開していた行検索
                const index = state.sendItems.findIndex(e => e.sequence === state.beforeExpandItem[0].sequence);
                // 行データ更新
                state.sendItems[index] = state.beforeExpandItem[0];
                // 更新前データ初期化
                tmpEscapeExpand.title = '';
                tmpEscapeExpand.message = '';
                // ダイアログデータ初期化
                state.tmpTitle = '';
                state.tmpMessage = '';
                state.isUseEscapeExpand = false;
                expandForm.value.reset();
            }
            state.beforeExpandItem = [];
            // state.afterExpandItemに値が入っている（展開された行が変わった）場合
            if (state.afterExpandItem.length !== 0) {
                state.expanded = state.afterExpandItem;
                openExpand(state.expanded[0]);
                return;
            }
        }

        /**
         * 全選択チェックボックス押下時処理
         * @param isSelected 全選択チェックボックス選択状態
         */
        async function selectAll(isSelected: boolean) {
            // state.total が0でない場合
            if (state.total !== 0) {
                // 全選択チェックボックスがtrueの場合
                if (isSelected) {
                    // ローダーダイアログの表示
                    state.isLoadingSelectAll = true;

                    // 全ページ分の組織IDデータ取得APIで組織IDを取得する
                    //   GET /api/important-information/check-all
                    //     パラメータ：組織マスタフラグ（0）
                    //                フィルタ条件（{}）
                    //     レスポンス：response
                    const response = await ctx.root.$store.dispatch('importantInformation/getCheckAllOrganizationId',
                    {
                        isOrganizationsFlag: SystemConst.IS_ORGANIZATIONS_FLAG.TEMPORARY_IMPORTANT_INFORMATION,
                        filterParams: {}
                    });

                    // レスポンスのHTTPステータスコードが200以外の場合
                    if (response.status !== HttpStatus.OK) {
                        // ローダーダイアログを非表示にする
                        state.isLoadingSelectAll = false;
                        // エラー画面を表示し、処理を終了する
                        commonRef.value.errorDisplayTransitionControl(response);
                    }

                    // 正常に処理が終了した場合（HTTPステータスコード：200）
                    // チェック済み送付先組織に取得したデータを格納する
                    state.tmpSelectedData = response.data.organizationIdList;
                }

                // 全選択チェックボックスがfalseの場合
                if (!isSelected) {
                    // チェック済み送付先組織を初期化する
                    state.tmpSelectedData = {};
                }
    
                // 送付先組織一覧の個別チェックボックスの状態を更新する
                checkUpdate();
            }
            // ローダーダイアログを非表示にする
            state.isLoadingSelectAll = false;
        }

        /**
         * 行ごとのチェックボックス押下時処理
         * @param selectedItem 選択した行のデータ
         */
        function select(selectedItem: CreateSendFilesList) {
            // 選択された行のチェックボックスにチェックを入れ、チェック済み送付先組織に選択された組織IDが存在しない場合
            if (selectedItem.dataTableSelect && !(selectedItem.organizationId in state.tmpSelectedData)) {
                // チェック済み送付先組織に組織IDを追加する
                state.tmpSelectedData[selectedItem.organizationId] = {
                    organizationId: selectedItem.organizationId,
                    affiliationCode: selectedItem.affiliationCode
                };
            }
            
            // 選択された行のチェックボックスからチェックを外し、チェック済み送付先組織に選択された組織IDが存在する場合
            if (!selectedItem.dataTableSelect && (selectedItem.organizationId in state.tmpSelectedData)) {
                // チェック済み送付先組織から組織IDを削除する
                delete state.tmpSelectedData[selectedItem.organizationId];
            }

            // 送付先組織一覧の全選択チェックボックスの状態を更新する
            checkAllUpdate();
        }

        /**
         * チェック済み送付先組織の状態を、一覧のチェックボックスに反映する
         * state.tmpSelectedDataに格納されているチェック状態をstate.sendItemsに反映する
         */
        function checkUpdate() {
            // 一覧の現在閲覧している1ページの件数分ループを行う
            for (let i = 0; i < state.sendItems.length; i++) {
                // 一覧の組織IDが、チェック済み送付先組織に存在するかを判定する
                if (state.sendItems[i].organizationId in state.tmpSelectedData) {
                    // 存在する場合
                    ctx.root.$set(state.sendItems[i], 'dataTableSelect', true);
                } else {
                    // 存在しない場合
                    ctx.root.$set(state.sendItems[i], 'dataTableSelect', false);
                }
            }
        }

        /**
         * 全選択チェックボックスの更新処理
         */
        function checkAllUpdate() {
            // その他ページも含め、全ての行にチェックがついており、総件数が1以上の場合、全選択のチェックボックスのチェックを入れる
            if (Object.keys(state.tmpSelectedData).length === state.total && state.total > 0) {
                state.selectedItemAll = true;
            }
 
            // その他ページも含め、全ての行にチェックがついていない場合 または、総件数が0件の場合、全選択のチェックボックスのチェックを外す
            if (Object.keys(state.tmpSelectedData).length !== state.total || state.total === 0) {
                state.selectedItemAll = false;
            }
        }

        /**
         * 一括添付ファイル設定
         */
        async function attachmentZipSetting() {
            updateBulkAddAttachmentFile();
            // バリデーションチェックを行い、エラーの場合
            if (!state.isAttachmentFileValid) {
                // 以降の処理を行わない
                return;
            }

            // ダイアログを閉じる
            state.fileInputDialog = false;

            // APIへ渡すためにFilePondFileオブジェクトからFileオブジェクトを作る
            const attachmentFiles: File[]
                = attachmentPondRef.value.getFiles().map((e: FilePondFile) => e.file);

            // ローダーダイアログを表示する
            commonRef.value.state.isLoading = true;
            commonRef.value.state.showDialog = true;

            const tmpItemOrganizationId: string[] = [];
            // チェック済み送付先組織でループを行い、組織IDをtmpItemOrganizationId に格納する
            for (const key in state.tmpSelectedData) {
                tmpItemOrganizationId.push(state.tmpSelectedData[key].organizationId);
            }

            // 一括で添付ファイルのデータを登録する
            // POST /api/important-information/create-sending/file-data
            // パラメータ: forms（添付ファイル）
            //             一時重要情報ID
            //             更新対象シーケンスリスト
            //             全選択チェックボックスの値
            //             フォルダー名設定ラジオボタンの値
            const response = await ctx.root.$store.dispatch('importantInformation/setAttachmentFileData',
                {
                    attachmentFile: attachmentFiles,
                    tmpImportantInfoId: state.tmpImportantInfoId,
                    checkTmpItemOrganizationId: tmpItemOrganizationId,
                    isSelectedAll: state.selectedItemAll,
                    orgIdentification: state.inputFolderParam
                }
            );

            // ローダーダイアログを閉じる
            commonRef.value.state.isLoading = false;
            commonRef.value.state.showDialog = false;

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                if (response.status === HttpStatus.BAD_REQUEST) {
                    // バリデーションチェックでエラーが発生した場合（400のステータスコード）
                    // レスポンスのエラー内容を保存する
                    errors = response.data.errors;
                    // エラーダイアログ表示切替関数呼び出し
                    changeDialog();
                    return;
                } else {
                    // 400以外のステータスコードのエラーの場合
                    // エラーメッセージを表示して送付データ登録画面へ戻る
                    commonRef.value.errorDisplayTransitionControl(response);
                    return;
                }
            }

            // 行が展開されていた場合
            if (state.expanded.length !== 0) {
                state.expanded = [];
            }
            // フォルダー名設定ラジオボタンの値を初期化する
            state.inputFolderParam = SystemConst.ATTACHMENT_FOLDER_PARAM.AFFILIATE_CODE;

            // ページ情報が1ページ目でない場合
            if (pageNation.value !== 1) {
                // 1ページ目へ戻す
                pageNation.value = 1;
                state.pageOptions.page = 1;
            }

            // 添付ファイル一括ファイル設定フラグをtrueにする
            state.isAttachmentZipSetting = true;

            // チェック済み送付先組織の初期化
            state.tmpSelectedData = {};
            // テーブルを更新する
            getSendingData();

            // attachmentPondRefの中身を初期化する
            attachmentPondRef.value.removeFiles();
        }

        /**
         * ダイアログ表示切替
         */
        function changeDialog() {
            // エラー表示内容を初期化する
            state.fileErrorItems = [];
            // レスポンスデータ.errorsの件数分繰り返す
            for (let i = 0; i < errors.length; i++) {
                // エラーメッセージ一件取得して「、」で分割する
                const oneError = errors[i].split('、');
                // 「、」で分割した最初のデータが「添付」であることを確認する
                if (oneError[0] === SystemConst.FILE_DIVISION.ATTACHMENT) {
                    // 「、」で分割した真ん中のデータを'errorLocation'に設定する
                    // 「、」で分割した後ろのデータを'message'に設定する
                    state.fileErrorItems[i] = { errorLocation: oneError[1], message: oneError[2] };
                } else {
                    // 分割できず、「添付」にならなかった場合
                    // 'message'にエラーメッセージを格納する
                    state.fileErrorItems[i] = { errorLocation: '', message: oneError[0] };
                }
            }
            // エラーのない表示がない状態で表示された場合
            if (state.fileErrorItems.length === 0) {
                state.fileErrorItems[0] = { errorLocation: '', message: MessageConst.S_NO_ERROR_MESSAGE };
            }

            // エラーダイアログを表示する
            state.showErrorMessageDialog = true;
            return;
        }

        /**
         * 一括登録用添付ファイルへファイルを追加・削除された時のイベント
         */
        function updateBulkAddAttachmentFile() {
            // 添付ファイルのバリデーションを行う
            for (let index = 0; index < bulkAddAttachmentFileRules.length; index++) {
                const rule: any = bulkAddAttachmentFileRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // クリア中はバリデーションを実施しない
                    if (state.isClear) {
                        state.isClear = false;
                        break;
                    }
                    // エラーあり
                    // エラーメッセージ表示
                    state.attachmentFileErrorMessage = valid;
                    state.isAttachmentFileValid = false;
                    break;
                } else {
                    // エラーなし
                    state.attachmentFileErrorMessage = '';
                    state.isAttachmentFileValid = true;
                }
            }
        }

        /**
         * 行展開用添付ファイルへファイルを追加・削除された時のイベント
         */
        function updateExpandAttachmentFiles() {
            // 添付ファイルのバリデーションを行う
            for (let index = 0; index < expandAttachmentFilesRules.length; index++) {
                const rule: any = expandAttachmentFilesRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // クリア中はバリデーションを実施しない
                    if (state.isClearExpand) {
                        state.isClearExpand = false;
                        break;
                    }
                    // エラーあり
                    // エラーメッセージ表示
                    state.attachmentDetailFileErrorMessage = valid;
                    state.isAttachmentDetailFileValid = false;
                    break;
                } else {
                    // エラーなし
                    state.attachmentDetailFileErrorMessage = '';
                    state.isAttachmentDetailFileValid = true;
                }
            }
        }

        /**
         * 一括タイトル・メッセージ閉じるボタン押下時処理
         */
        function collectiveClose() {
            // ダイアログを閉じる
            state.collectiveSettingDialog = false;
            // 入力したデータを初期化する
            state.collectiveTitle = '';
            state.collectiveMessage = '';
        }

        /**
         * 一括タイトル・メッセージ設定処理
         */
        async function collectiveSettingTitleMessage() {
            // バリデーションチェックを実行
            if (!bulkForm.value.validate()) {
                // エラーの場合処理を行わない
                return;
            }

            // ダイアログを閉じる
            state.collectiveSettingDialog = false;

            const tmpItemOrganizationId: string[] = [];
            // チェック済み送付先組織でループを行い、組織IDをtmpItemOrganizationId に格納する
            for (const key in state.tmpSelectedData) {
                tmpItemOrganizationId.push(state.tmpSelectedData[key].organizationId);
            }
            // APIを実行し、タイトルとメッセージを一括で設定する
            // PUT /api/important-information/create-sending/set-collective
            const response = await ctx.root.$store.dispatch('importantInformation/updateCollective',
                {
                    tmpImportantInfoId: state.tmpImportantInfoId,
                    checkTmpItemOrganizationId: tmpItemOrganizationId,
                    collectiveTitle: state.collectiveTitle,
                    collectiveMessage: state.collectiveMessage,
                    isSelectedAll: state.selectedItemAll
                });

            // レスポンスが正常でない場合
            if (response.status !== HttpStatus.OK) {
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 行が展開されていた場合
            if (state.expanded.length !== 0) {
                state.beforeExpandItem = [];
                state.expanded = [];
            }
            // 一括設定ダイアログの値初期化
            state.collectiveTitle = '';
            state.collectiveMessage = '';
            // 個別設定ダイアログの値も削除
            tmpEscapeExpand.title = '';
            tmpEscapeExpand.message = '';

            // ページ情報が1ページ目でない場合
            if (pageNation.value !== 1) {
                // ページ番号を1へ戻す
                state.pageOptions.page = 1;
                pageNation.value = 1;
            }

            // チェック済み送付先組織の初期化
            state.tmpSelectedData = {};
            // テーブルを更新する
            getSendingData();
        }

        /**
         * 個別タイトル・メッセージ閉じるボタン押下時処理
         */
        function individualClose() {
            // ダイアログを閉じる
            state.individualSettingDialog = false;
            // 入力したデータを初期化する
            state.tmpTitle = '';
            state.tmpMessage = '';
            state.isUseEscapeExpand = false;
            expandForm.value.reset();
        }

        /**
         * 個別タイトル・メッセージ設定処理
         */
        function individualSettingTitleMessage() {
            // バリデーションチェックを実行
            if (!expandForm.value.validate()) {
                // エラーの場合処理を行わない
                return;
            }

            // 一度目の更新の際に展開中のタイトル・メッセージを tmpEscapeExpandに保存する
            if (!state.isUseEscapeExpand) {
                state.isUseEscapeExpand = true;
                tmpEscapeExpand.title = state.expanded[0].expandItem.title;
                tmpEscapeExpand.message = state.expanded[0].expandItem.message;
            }

            // 展開中のタイトル・メッセージを設定した値に置き換える
            state.expanded[0].expandItem.title = state.tmpTitle;
            state.expanded[0].expandItem.message = state.tmpMessage;

            // ダイアログを閉じる
            state.individualSettingDialog = false;
        }

        /**
         * 展開行のキャンセルボタン押下時処理
         */
        function deleteSavedData() {
            let isUpdate = false;
            const files: FilePondFile[] = expandPondRef.value.getFiles();
            // 以下条件のいずれかが当てはまる場合
            //   expandPondRefのファイル件数がexpand[0].expandItem.attachmentFilsesの件数と一致していない
            //   expandPondRefのtype: 'input'のデータがある
            //     更新データありフラグを立てる
            if (files.length !== state.expanded[0].expandItem.attachmentFiles.length ||
                files.findIndex(e => e.origin === FileOrigin.INPUT) !== -1) {
                isUpdate = true;
            }

            // タイトル・メッセージのどちらか一方でも更新されていた場合
            if (!(state.tmpTitle === null || state.tmpTitle === '' || state.tmpMessage === '' || state.tmpMessage === null)) {
                // 更新データありフラグを立てる
                isUpdate = true;
            }

            if (isUpdate) {
                // 確認ダイアログを表示する
                state.closeWarningDialog = true;
            } else {
                state.expanded = [];
            }
        }

        /**
         * 展開行の設定ボタン押下時処理
         */
        async function savedDataSetting() {
            const files: FilePondFile[] = expandPondRef.value.getFiles();
            // 以下条件のいずれかが当てはまる場合、バリデーションチェック
            //   expandPondRefのファイル件数が0
            //   expandPondRefのファイル件数がstate.expand[0].expandItem.attachmentFilsesの件数と一致していない
            //   expandPondRefのtype: 'input'のデータがある
            if (files.length === 0 ||
                files.length !== state.expanded[0].expandItem.attachmentFiles.length ||
                files.findIndex(e => e.origin === FileOrigin.INPUT) !== -1) {
                // 添付ファイルのバリデーションチェック
                updateExpandAttachmentFiles();
                // バリデーションエラーの場合
                if (state.isAttachmentDetailFileValid === false) {
                    // 処理をしない
                    return;
                }
            }

            let displayMessage = '';
            if (!state.expanded[0].expandItem.title) {
                // タイトル未入力エラー設定
                displayMessage = CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['タイトル']);
            }
            if (!state.expanded[0].expandItem.message) {
                // メッセージ未入力エラー設定
                if (!displayMessage) {
                    displayMessage = CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['メッセージ']);
                } else {
                    displayMessage += `\n`;
                    displayMessage += CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['メッセージ']);
                }
            }
            if (!displayMessage) {
                // エラーが表示されていない場合、確認ダイアログを表示する
                displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['送付データの設定']);
                commonRef.value.displayConfirmationMessageDialog(displayMessage);
            } else {
                // エラーメッセージ表示
                commonRef.value.simpleDisplayMessageDialog(displayMessage);
            }
        }

        /**
         * 展開行の確認ダイアログの「はい」クリック時処理
         */
        async function processingData() {
            // 添付ファイル取得
            const files: FilePondFile[] = expandPondRef.value.getFiles();
            // DB上のファイルが、files（アップロードするファイル）にないものは
            // state.expanded[0].expandItem.attachmentFiles から消す
            const newFiles: FileData[] = [];
            state.expanded[0].expandItem.attachmentFiles.forEach(e => {
                if (files.filter(x => x.file.name === e.fileName).length !== 0) {
                    newFiles.push(e);
                }
            });
            state.expanded[0].expandItem.attachmentFiles = newFiles;

            // ただし、同じファイル名の場合は、更新された（入れ替えた）ファイルと判断し、ファイルサイズを削除する
            files.forEach(e => {
                if (e.origin === FileOrigin.INPUT) {   // 'INPUT' = ユーザー入力のファイル
                    if (state.expanded[0].expandItem.attachmentFiles.filter(x => x.fileName === e.file.name).length !== 0) {
                        const r = state.expanded[0].expandItem.attachmentFiles.find(x => x.fileName === e.file.name);
                        if (r) {
                            // ファイルサイズを削除
                            r.fileSize = undefined;
                        }
                    }
                }
            });
            // 追加する格納中のファイルデータ
            // アップロードする全てのファイルのFileオブジェクトを作る
            const realFiles: File[] = expandPondRef.value.getFiles().map((e: FilePondFile) => e.file);

            // 展開中シーケンス取得
            const openSeq = state.expanded[0].sequence;

            // 展開行データ更新API呼び出し
            const response = await ctx.root.$store.dispatch('importantInformation/updateExpandInformation',
                {
                    formData: {
                        'tmpImportantInfoId': state.tmpImportantInfoId,
                        'checkTmpItemSequence': openSeq,
                        'title': state.expanded[0].expandItem.title,
                        'message': state.expanded[0].expandItem.message,
                        'files': state.expanded[0].expandItem.attachmentFiles
                    },
                    attachmentFiles: realFiles
                }
            );

            // APIが正常でなかった場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 成功メッセージダイアログ表示
            commonRef.value.displayMessageDialog(response);
            // 正常の場合
            // state.expandedを空にする
            state.beforeExpandItem = [];
            state.expanded = [];
            // タイトル・メッセージの一時保存データ初期化
            state.isUseEscapeExpand = false;
            state.tmpTitle = '';
            state.tmpMessage = '';
            // 完了データリストにまだない場合にシーケンスを格納
            if (completionMarkData.findIndex(e => e === openSeq) === -1) {
                completionMarkData.push(openSeq);
            }
            // チェック済み送付先組織の初期化
            state.tmpSelectedData = {};
            // 一覧データ再取得
            getSendingData();
        }

        /**
         * 「戻る」ボタンクリック処理
         */
        function back() {
            // 組織選択へ遷移
            router.push('/important-information/select-organization');
        }

        /**
         * 「登録」ボタンクリック時処理
         */
        function sendData() {
            // 完了マークがすべてのデータについているか判別
            if (!state.isAllCompletionMark) {
                // ついていなかった場合
                // ダイアログに「未設定のデータがあるため、登録できません。」と表示して終了する
                commonAddRef.value.simpleDisplayMessageDialog(MessageConst.E_SET_DATA);
                return;
            }

            // 確認ダイアログを表示する
            const confirmationMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['送付データの登録']);
            // 確認メッセージダイアログ表示
            //   「はい」をクリックした場合はprocessingDataAdd()メソッドが呼び出され、
            //   「いいえ」をクリックした場合はダイアログを閉じ、送付データ登録へ戻る
            commonAddRef.value.displayConfirmationMessageDialog(confirmationMessage);
        }

        /**
         * 登録ボタンの確認ダイアログで「はい」押下時処理
         */
        async function processingDataAdd() {
            // 完了アイコンがついているデータを登録APIを実行する
            const response = await ctx.root.$store.dispatch('importantInformation/addSendData', {
                tmpImportantInfoId: state.tmpImportantInfoId
            });

            // APIが正常でなかった場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonAddRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 登録完了へ遷移
            router.push('/important-information/create-complete');
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            tableHeight,
            linesPerPage,
            pageNation,
            itemsPerPage,
            sortBy,
            sortDesc,
            tableLoadingText,
            displayMessage,
            state,
            form,
            bulkForm,
            expandForm,
            stepper,
            router,
            attachmentPondRef,
            expandPondRef,
            commonRef,
            commonAddRef,
            selectCreation,
            attachmentTreeFormat,
            maxLength,
            titleRules,
            messageRules,
            updateBulkAddAttachmentFile,
            updateExpandAttachmentFiles,
            updateExpand,
            openExpand,
            closeExpand,
            updateOptions,
            selectAll,
            select,
            collectiveClose,
            collectiveSettingTitleMessage,
            individualClose,
            individualSettingTitleMessage,
            deleteSavedData,
            savedDataSetting,
            processingData,
            attachmentZipSetting,
            back,
            sendData,
            processingDataAdd,
            onResize
        };
    }
});
